import React from 'react';
import { get, find, cloneDeep, merge, debounce } from 'lodash';

// React view component to render
import { ServiceNowService } from 'services/serviceNow';
import { serviceNow as ServiceNowView } from 'ui/views/widgets/serviceNow';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import {  SERVICE_RESPONSE_STATUS, SIZES, ERROR_RESPONSE_TEMPLATE } from 'constants/common';
import * as errorOverlay from 'utils/errorOverlay';
import * as successOverlay from 'utils/successOverlay';
import { getLabel } from 'utils/getLabel';
import { popover } from 'modules/popover';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { ServiceNowQuickForm } from 'ui/containers/ServiceNowQuickForm';
 *
 * // render
 * <ServiceNowQuickForm propName='propValue'/>
 */
export class ServiceNowQuickForm extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        this.__bind();

        this.serviceNowService = new ServiceNowService();

        this.state = {
            ...this.props,
            values: {},
            disableSubmit: true,
            closePopoverOnScrollTrigger: 0,
            initialDataFetched: false
        };
    }

    /**
     * @desc Bind functions
     */
     __bind() {
        this.handleFormSubmit = this.handleFormSubmit.bind( this );
        this.handleFormScroll = debounce( this.handleFormScroll, 500, { leading: true, trailing: false } ).bind( this );
        this.fetchInitialData = this.fetchInitialData.bind( this );
    }

    componentDidMount() {

        // show page level preloader
        pageLevelPreloader.show();

        this.fetchInitialData();
    }

    /**
     * @desc Executes on scroll event in form
     */
     handleFormScroll() {
        log.debug( 'ServiceNow.handleFormScroll()' );

        // toggle the state of trigger
        this.setState( state => {
            return { closePopoverOnScrollTrigger: state.closePopoverOnScrollTrigger ? 0 : 1 };
        } );
    }

     /**
     * @desc Handles next action click
     */
      async fetchInitialData() {
        log.debug( 'ServiceNowQuickForm.handleNextActionClick()' );

        const formCategoryValues = get( this.props, 'requestData.data', {} );

        const url = get( this.props, 'url', {} );
    
        // get status and widget data corresponding to above selected value
        const { status, data } = await this.serviceNowService.fetchForm( {
            url,
            data: {
                context: get( this.props, 'context', {} ),
                data: formCategoryValues
            }
        } );

        // hide page level preloader
        pageLevelPreloader.hide();
        
        // check if status was success
        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // clone current state
            const _state = cloneDeep( this.state );

            _state.options = merge( get( _state, 'options', [] ), data.options );

            const newValues = merge( formCategoryValues, data.values );

            _state.values = newValues;

            _state.actions = data.actions;
            _state.fields =  data.fields;
            _state.disableSubmit = false;

            _state.initialDataFetched = true;

            // set updated state
            this.setState( _state );
        } else {

            this.props.closeOverlay();

            // show error overlay
            errorOverlay.open();
        }
    }

    /**
     * @desc Handles form submit
     */
     async handleFormSubmit ( formData ) {
        log.debug( 'ServiceNow.handleFormSubmit()' );

        const context = get( this.props, 'context', {} );
        const successAction = find( this.state.actions, { name: 'submit' } );

        // cancel ongoing service
        this.serviceNowService.cancel();

        // show page preloader
        pageLevelPreloader.show();

        this.props.closeOverlay();

        const formCategoryValues = get( this.props, 'requestData.data', {} );

        const { status, data } = await this.serviceNowService.submitFormData( {
            url: successAction.url,
            data: {
                context,
                data: merge( formData, formCategoryValues )
            }
        } );

        // hide page preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // get sub message from response data
            const subMessage = getLabel( get( data, 'message' ), get( data, 'data' ) );

            // show success overlay
            successOverlay.open( {
                message: getLabel( get( data, 'title' ) ),
                config: {
                    autoClose: false,
                    overlaySize: SIZES.LARGE,
                    messageSize: SIZES.LARGE,
                    subMessage
                }
            } );

        } else {
            const template = get( data, 'template', ERROR_RESPONSE_TEMPLATE.DEFAULT );

            if( template === ERROR_RESPONSE_TEMPLATE.notification ) {
                errorOverlay.open( { message: get( data, 'description', '' ), size: SIZES.SMALL } );
            } else {
                errorOverlay.open();
            }
        }
    }

    componentWillUnmount() {
        popover.close();
    }

    /**
     * @desc Render a React view component
     */
    render() {

        if( !this.state.initialDataFetched ) {
            return null;
        } else {
            return (
                <ServiceNowView
                    { ...this.state }
                    onFormSubmit = { this.handleFormSubmit }
                    onScroll={ this.handleFormScroll }
                />
            );
        }
    }
}

// set display name
ServiceNowQuickForm.displayName = 'ServiceNowQuickForm';

// set default props
ServiceNowQuickForm.defaultProps = {};

// prop types
ServiceNowQuickForm.propTypes = {};
